import { Component } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { DeleteDeviceDialogComponent } from 'src/app/delete-device-dialog/delete-device-dialog.component';
import { DeviceDialogComponent } from 'src/app/device-dialog/device-dialog.component';
import { ApiGatewayService, DeviceMessage, RegisteredDevice } from 'src/app/services/api-gateway.service';
import { InfoMessageService, delay, emitInfoMessage } from 'src/app/services/info-message.service';
import { DateTimeUtilsService } from 'src/app/services/date-time-utils.service';

export interface DialogData {
  dialogType : 'update' | 'register'
  deviceData : RegisteredDevice
}

export interface DialogRemoveData {
  deviceData : RegisteredDevice
}

@Component({
    selector: 'tab-devices',
    templateUrl: './tab-devices.component.html',
    styleUrls: ['./tab-devices.component.scss'],
    standalone: false
})
export class TabDevicesComponent {
  devices?: RegisteredDevice[]
  messages?: DeviceMessage[]
  selectedDevice? : RegisteredDevice
  loadingDevices: boolean = true
  loadingMessages: boolean = false
  sortField: 'description' | 'last_seen' = 'description'
  sortDirection: 'asc' | 'desc' = 'asc'

  constructor(private infoMessageService: InfoMessageService,
    private gatewayApiService: ApiGatewayService,
    public dialog: MatDialog,
    public dateTimeUtils: DateTimeUtilsService){
    }

  async ngOnInit(){
    // Get Devices
    this.getRegisteredDevicesUser();
  }

  async getRegisteredDevicesUser(){
    await this.gatewayApiService.getRegisteredDevices()
    .then((data) => {
      if (data.body){
        this.devices = data.body
        this.applySort()
        if (data.body.length == 0){
          emitInfoMessage("No nodes found for user", this.infoMessageService)
        }
      }
      this.loadingDevices = false;
    })
    .catch((error) => {
      this.loadingDevices = false;
      emitInfoMessage(error.status +": "+ error.statusText, this.infoMessageService)
    })
  }

  async deleteRegisteredDevice(toDeleteDevice:RegisteredDevice){
    if (this.selectedDevice === toDeleteDevice){
      this.selectedDevice = undefined;
    }
    await this.gatewayApiService.deleteRegisteredDevice(toDeleteDevice)
    .then(() => {
      emitInfoMessage("Deleted device", this.infoMessageService)
      delay(500);
      this.getRegisteredDevicesUser();
    })
    .catch((error) => {
      emitInfoMessage(error.status +": "+ error.statusText, this.infoMessageService)
    })
  }

  async confirmDelete(toDeleteDevice:RegisteredDevice) {
    // Open dialog with existing or new made device as data
    const dialogRef = this.dialog.open(DeleteDeviceDialogComponent, {
      data : { deviceData : toDeleteDevice}
    });

    // Wait for first value from dialog and save
    let dialogResult = await firstValueFrom(dialogRef.afterClosed())

    // If dialog was clicked on submit (returning a true bool), i.e. not cancelled
    if (dialogResult){
      this.deleteRegisteredDevice(toDeleteDevice)
    }
  }

  async getDeviceMessages(device : RegisteredDevice){
    this.selectedDevice = device
    this.loadingMessages = true
    await this.gatewayApiService.getDeviceMessages(device)
    .then((data) => {
      if (data.body){
        this.messages = data.body.reverse()
        if (data.body.length == 0){
          emitInfoMessage("No messages found for node", this.infoMessageService)
        }
      }
      this.loadingMessages = false
    })
    .catch((error) => {
      emitInfoMessage(error.status +": "+ error.statusText, this.infoMessageService)
      this.loadingMessages = false
    })
  }

  async openDialogDevice(dialogType : 'update' | 'register', toUpdateDevice? : RegisteredDevice) {
    // For register we make a new empty device
    if (!toUpdateDevice) toUpdateDevice = {description:'', devaddr:'', network_key: ''}

    // Open dialog with existing or new made device as data
    const dialogRef = this.dialog.open(DeviceDialogComponent, {
      data : {dialogType : dialogType, deviceData : toUpdateDevice}
    });

    // Wait for first value from dialog and save
    let dialogResult = await firstValueFrom(dialogRef.afterClosed())

    // If dialog was clicked on submit (returning a true bool), i.e. not cancelled
    if (dialogResult){
      if (dialogType == 'register'){
        this.addRegisteredDeviceDialog(toUpdateDevice)
      } else if (dialogType == 'update'){
        this.updateRegisteredDeviceDialog(toUpdateDevice)
      } else {
        emitInfoMessage("Cannot handle return values from dialog", this.infoMessageService)
      }
    }
  }

  async addRegisteredDeviceDialog(newDevice : RegisteredDevice) {
    await this.gatewayApiService.addRegisteredDevice(newDevice)
    .then(() => {
      emitInfoMessage("Added node", this.infoMessageService)
      delay(500);
      this.getRegisteredDevicesUser();
    })
    .catch((error) => {
      emitInfoMessage(error.status +": "+ error.statusText, this.infoMessageService)
    })
  }

  async updateRegisteredDeviceDialog(toUpdateDevice : RegisteredDevice){
    if (!toUpdateDevice){
      emitInfoMessage("Error in updating node, no node found to edit", this.infoMessageService)
      return
    }
    await this.gatewayApiService.updateRegisteredDevice(toUpdateDevice)
    .then(() => {
      emitInfoMessage("Updated node", this.infoMessageService)
      delay(500);
      this.getRegisteredDevicesUser();
    })
    .catch((error) => {
      emitInfoMessage(error.status +": "+ error.statusText, this.infoMessageService)
    })
  }

  toggleSort(field: string) {
    const f = field as 'description' | 'last_seen'
    if (this.sortField === f) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
    } else {
      this.sortField = f
      this.sortDirection = 'asc'
    }
    this.applySort()
  }

  private applySort() {
    this.devices?.sort((a, b) => {
      const aVal = (a[this.sortField] ?? '').toLowerCase()
      const bVal = (b[this.sortField] ?? '').toLowerCase()
      if (!aVal && bVal) return 1
      if (aVal && !bVal) return -1
      const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0
      return this.sortDirection === 'asc' ? cmp : -cmp
    })
  }

  isSelectedDevice(device: RegisteredDevice): string {
    if(device === this.selectedDevice){
      return "bg-[#E6DBF9] dark:bg-[#250B47] dark:text-white selected-device"
    }
    return "bg-white dark:bg-[#171717] dark:text-white"
  }
}
